<script lang="ts">
  import { tick, untrack } from 'svelte';
  import type { Message as Msg } from '../lib/api';
  import { dayKey, dayLabel, GROUP_WINDOW_MS } from '../lib/format';
  import { app } from '../lib/stores.svelte';
  import Message from './Message.svelte';

  let {
    conversationId,
    focus = null,
    archived = false,
  }: { conversationId: string; focus?: string | null; archived?: boolean } = $props();

  let scroller: HTMLDivElement | undefined = $state();
  /** The bottom is visible: new messages scroll into view and the conversation is read. */
  let atBottom = $state(true);
  let error = $state('');
  let highlight = $state<string | null>(null);

  // Read only: the entry is created by the loaders (state may not change inside a $derived).
  const EMPTY = { items: [] as Msg[], hasMore: true, hasNewer: false, loading: true, focusId: null };
  const timeline = $derived(app.timelines[conversationId] ?? EMPTY);
  // Where « Nouveaux messages » goes: the read position when the conversation was opened.
  // Captured once: recreated for each conversation ({#key}), and the line must not move
  // when the conversation is marked read.
  const readMark = untrack(() => app.conversation(conversationId)?.last_read_at ?? '');

  type Row =
    | { kind: 'day'; key: string; label: string }
    | { kind: 'new'; key: string }
    | { kind: 'message'; key: string; message: Msg; grouped: boolean };

  const rows = $derived.by(() => {
    const out: Row[] = [];
    let previous: Msg | undefined;
    let newShown = false;
    for (const message of timeline.items) {
      const day = dayKey(message.created_at);
      const newDay = !previous || dayKey(previous.created_at) !== day;
      if (newDay) out.push({ kind: 'day', key: `day-${day}`, label: dayLabel(message.created_at) });
      const isNew =
        !newShown && !!readMark && message.created_at > readMark && message.author.id !== app.me?.id && message.kind !== 'system';
      if (isNew) {
        out.push({ kind: 'new', key: 'new' });
        newShown = true;
      }
      const grouped =
        !newDay &&
        !isNew &&
        !!previous &&
        previous.kind === 'user' &&
        message.kind === 'user' &&
        previous.author.id === message.author.id &&
        Date.parse(message.created_at) - Date.parse(previous.created_at) < GROUP_WINDOW_MS;
      out.push({ kind: 'message', key: message.id, message, grouped });
      previous = message;
    }
    return out;
  });

  function scrollToBottom() {
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }

  async function showFocused(id: string | null) {
    await tick();
    const element = id ? scroller?.querySelector(`[data-message-id="${CSS.escape(id)}"]`) : null;
    if (!element) return scrollToBottom();
    element.scrollIntoView({ block: 'center' });
    highlight = id;
    setTimeout(() => (highlight = null), 2000);
  }

  async function loadOlder() {
    if (!scroller) return;
    const before = scroller.scrollHeight;
    try {
      if (await app.loadOlder(conversationId)) {
        await tick();
        // Keep the messages on screen where they were.
        scroller.scrollTop += scroller.scrollHeight - before;
      }
    } catch {
      error = 'Impossible de charger les messages plus anciens';
    }
  }

  function onScroll() {
    if (!scroller) return;
    const fromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    atBottom = fromBottom < 80;
    if (scroller.scrollTop < 300 && timeline.hasMore && !timeline.loading) void loadOlder();
    if (fromBottom < 300 && timeline.hasNewer && !timeline.loading) {
      void app.loadNewer(conversationId).catch(() => (error = 'Impossible de charger les messages suivants'));
    }
  }

  async function backToLatest() {
    await app.loadLatest(conversationId, true);
    await tick();
    scrollToBottom();
  }

  // First page (or a page around the message to show), then position the list.
  $effect(() => {
    const id = conversationId;
    const target = focus;
    error = '';
    untrack(() => {
      const t = app.timelines[id];
      const load = target
        ? app.jumpTo(id, target).then(async () => {
            const t = app.timelines[id];
            await showFocused(t?.focusId ?? null);
            // Shown: from now on, new messages are followed as usual (when at the end).
            if (t) t.focusId = null;
          })
        : app.loadLatest(id, !!t?.hasNewer).then(async () => {
            await tick();
            scrollToBottom();
            // A short first page may not fill the screen: fetch more until it scrolls.
            while (scroller && scroller.scrollHeight <= scroller.clientHeight && (await app.loadOlder(id))) {
              await tick();
              scrollToBottom();
            }
          });
      load.catch(() => (error = 'Impossible de charger les messages'));
    });
  });

  // New messages: follow them if the bottom was visible (never on an older page).
  $effect.pre(() => {
    void timeline.items.length;
    if (atBottom && !timeline.hasNewer && !timeline.focusId) tick().then(scrollToBottom);
  });

  // Read when the bottom of the live conversation is visible and the window has the focus.
  $effect(() => {
    const last = timeline.items.at(-1);
    if (atBottom && app.focused && last && !timeline.hasNewer) void app.markRead(conversationId, last).catch(() => {});
  });
</script>

<div class="scroller" bind:this={scroller} onscroll={onScroll}>
  <div class="column">
    {#if timeline.loading && timeline.items.length > 0}
      <p class="status">Chargement…</p>
    {:else if !timeline.hasMore && timeline.items.length > 0}
      <p class="status">Début de la conversation</p>
    {/if}
    {#each rows as row (row.key)}
      {#if row.kind === 'day'}
        <div class="separator" role="separator"><span>{row.label}</span></div>
      {:else if row.kind === 'new'}
        <div class="separator new" role="separator"><span>Nouveaux messages</span></div>
      {:else}
        <Message message={row.message} grouped={row.grouped} {archived} highlighted={highlight === row.message.id} />
      {/if}
    {/each}
    {#if !timeline.loading && timeline.items.length === 0 && !error}
      <p class="status">Aucun message pour l'instant.</p>
    {/if}
    {#if error}<p class="error" role="alert">{error}</p>{/if}
  </div>
</div>
{#if timeline.hasNewer}
  <div class="latest">
    <button type="button" class="btn" onclick={backToLatest}>Revenir aux messages récents ↓</button>
  </div>
{/if}

<style>
  .scroller {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 0 8px;
  }
  .column {
    max-width: calc(var(--reading-max) + 60px);
    padding: 0 12px;
    display: flex;
    flex-direction: column;
  }
  .separator {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 12px 12px 4px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: var(--muted);
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .separator.new {
    color: var(--accent-text);
  }
  .separator.new::before,
  .separator.new::after {
    background: var(--accent);
  }
  .status {
    margin: 8px 12px;
    color: var(--muted);
    font-size: 13px;
    text-align: center;
  }
  .error {
    margin: 8px 12px;
  }
  .latest {
    display: flex;
    justify-content: center;
    padding: 4px 0;
  }
  .latest .btn {
    height: 32px;
    font-size: 13px;
    border-radius: 16px;
  }
</style>
